import { inject, Injectable, signal } from '@angular/core';
import { PARTNER_SETTINGS } from '@constants/partner-settings.constants';
import { StoreDbModel } from '@db-models/store-db.model';
import { StoreScopedWorker } from '@db-models/store-scope.model';
import { FeatureService } from '@feature-services/feature.service';
import { PartnerSettingService } from '@feature-services/partner-setting.service';
import { PermissionService } from '@feature-services/permission.service';
import { StoreService } from '@feature-services/store.service';
import { callerCanActOnStoreId, callerCanActOnWorker, workerVisibleStoreIds } from '@util-services/store-scope.util';
import { catchError, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';

/**
 * Resolves, once per page load, whether the Store Hierarchy add-on is active and the partner's
 * store list, and answers "may the logged-in worker act on this worker / store" with the backend's
 * rule (cakephp `getVisibleStoreIds()` / `workersShareStore(..., includeDescendants: true)`).
 *
 * PermissionService must never inject this service (it would be a cycle) - it receives the visible
 * set as a parameter instead.
 */
@Injectable({
  providedIn: 'root'
})
export class StoreScopeService {

  private featureService = inject(FeatureService);
  private partnerSettingService = inject(PartnerSettingService);
  private permissionService = inject(PermissionService);
  private storeService = inject(StoreService);

  private readonly hierarchyEnabledState = signal(false);
  private readonly storesState = signal<StoreDbModel[]>([]);

  private readonly resolvedState = signal(false);

  readonly hierarchyEnabled = this.hierarchyEnabledState.asReadonly();
  readonly stores = this.storesState.asReadonly();
  /** True once the context for this session is known; gates answer with the add-on-off rule until then. */
  readonly resolved = this.resolvedState.asReadonly();

  private load$: Observable<void> | null = null;
  private generation = 0;
  private visibleSetMemo = new Map<string, ReadonlySet<number>>();

  /**
   * Resolves the hierarchy flag and the store list. Idempotent and shared between subscribers; never
   * errors (a failure leaves the hierarchy off and the next call retries).
   */
  load(): Observable<void> {
    if (!this.featureService.hasFeature('is_store_hierarchy_enabled')) {
      this.applyContext(false, []);
      this.resolvedState.set(true);
      return of(undefined);
    }

    if (!this.load$) {
      const generation = this.generation;
      this.load$ = this.partnerSettingService.getPartnerSettingsByIds([PARTNER_SETTINGS.ENABLE_STORE_HIERARCHY])
        .pipe(
          map(settings => !!Number(settings?.find(s => Number(s.setting_id) === PARTNER_SETTINGS.ENABLE_STORE_HIERARCHY)?.value)),
          switchMap(enabled => {
            if (enabled && this.permissionService.hasPermission(['read:stores', 'read:own:stores'])) {
              return this.storeService.all(true).pipe(map(stores => ({ enabled, stores: stores ?? [] })));
            }
            // without the store list there is no subtree to walk, so the visible set is the caller's own
            // stores: the hierarchy-off answer for a caller with a store, and - like cakephp, which never
            // lets two store-less workers share - nobody but themselves for a store-less caller
            return of({ enabled, stores: [] as StoreDbModel[] });
          }),
          tap(({ enabled, stores }) => {
            // a reset since this load started means the response belongs to a previous session
            if (generation === this.generation) {
              this.applyContext(enabled, stores);
              this.resolvedState.set(true);
            }
          }),
          map(() => undefined),
          catchError(() => {
            if (generation === this.generation) {
              this.load$ = null;
              this.applyContext(false, []);
              this.resolvedState.set(true);
            }
            return of(undefined);
          }),
          shareReplay(1),
        );
    }
    return this.load$;
  }

  /** Back to the not-loaded state (logout, partner switch). */
  reset(): void {
    this.generation++;
    this.load$ = null;
    this.applyContext(false, []);
    this.resolvedState.set(false);
  }

  /** The caller's visible store ids, or null when the hierarchy is off (today's equality applies). */
  visibleStoreIdsFor(caller: StoreScopedWorker | null | undefined): ReadonlySet<number> | null {
    // read the signals first so OnPush templates track them
    const enabled = this.hierarchyEnabledState();
    const stores = this.storesState();
    if (!enabled) {
      return null;
    }
    if (!caller) {
      return new Set<number>();
    }
    const key = [caller.id, caller.store_id, (caller.stores ?? []).map(store => store.id).join(',')].join('|');
    let visible = this.visibleSetMemo.get(key);
    if (!visible) {
      visible = new Set(workerVisibleStoreIds(caller, stores, true).filter(id => id > 0));
      this.visibleSetMemo.set(key, visible);
    }
    return visible;
  }

  canActOnWorker(target: StoreScopedWorker | null | undefined, caller: StoreScopedWorker | null | undefined): boolean {
    return callerCanActOnWorker(caller, target, this.visibleStoreIdsFor(caller));
  }

  canActOnStore(storeId: number | null | undefined, caller: StoreScopedWorker | null | undefined): boolean {
    return callerCanActOnStoreId(caller, storeId, this.visibleStoreIdsFor(caller));
  }

  private applyContext(enabled: boolean, stores: StoreDbModel[]): void {
    this.visibleSetMemo.clear();
    this.storesState.set(stores);
    this.hierarchyEnabledState.set(enabled);
  }
}
